import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CameraCapture } from '../components/CameraCapture';
import { MapPin, Plus, Edit2, Trash2, Building2, Phone, User, Camera, Navigation, CheckCircle2, Clock, Image } from 'lucide-react';
import { toast } from 'sonner';

export default function BusinessPlaces() {
  const { api, currentUser, selectedBusiness } = useApp();
  const [places, setPlaces] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [cameraMode, setCameraMode] = useState(null);
  const [form, setForm] = useState({
    name: '', address: '', city: '', zone: '', place_type: 'office',
    contact_person: '', contact_phone: '', status: 'active',
    lat: null, lng: null, selfie_photo: null, place_photo: null
  });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (selectedBusiness) params.business_id = selectedBusiness;
      const res = await api.getBusinessPlaces(params);
      setPlaces(res.data);
    } catch (e) { console.error(e); }
  }, [api, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const filtered = places.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.city.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditing(null);
    setWizardStep(1);
    setCameraMode(null);
    setForm({
      name: '', address: '', city: '', zone: '', place_type: 'office',
      contact_person: '', contact_phone: '', status: 'active',
      lat: null, lng: null, selfie_photo: null, place_photo: null
    });
    setDialogOpen(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setForm(prev => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude })),
        (err) => console.warn('Geo error:', err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setWizardStep(4);
    setCameraMode(null);
    setForm({
      name: p.name, address: p.address, city: p.city, zone: p.zone || '',
      place_type: p.place_type || 'office', contact_person: p.contact_person || '',
      contact_phone: p.contact_phone || '', status: p.status,
      lat: p.lat || null, lng: p.lng || null,
      selfie_photo: p.selfie_photo || null, place_photo: p.place_photo || null
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Location name is required'); return; }
    try {
      if (editing) {
        await api.updateBusinessPlace(editing.id, {
          name: form.name, address: form.address, city: form.city, zone: form.zone,
          place_type: form.place_type, contact_person: form.contact_person,
          contact_phone: form.contact_phone, status: form.status
        });
        toast.success('Place updated');
      } else {
        const reqData = {
          name: form.name, address: form.address, city: form.city, zone: form.zone,
          place_type: form.place_type, contact_person: form.contact_person,
          contact_phone: form.contact_phone,
          lat: form.lat, lng: form.lng,
          selfie_photo: form.selfie_photo, place_photo: form.place_photo,
          requester_id: currentUser?.id || ''
        };
        if (selectedBusiness) reqData.business_id = selectedBusiness;
        await api.requestBusinessPlace(reqData);
        const isAdmin = currentUser?.role === 'business_head';
        toast.success(isAdmin ? 'Business location approved and added!' : 'Approval request submitted to seniors');
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business place?')) return;
    try {
      await api.deleteBusinessPlace(id);
      toast.success('Place removed');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleSelfieCapture = (photo, location) => {
    setForm(prev => ({
      ...prev,
      selfie_photo: photo,
      lat: location?.lat || prev.lat,
      lng: location?.lng || prev.lng
    }));
    setCameraMode(null);
    setWizardStep(3);
  };

  const handlePlacePhotoCapture = (photo) => {
    setForm(prev => ({ ...prev, place_photo: photo }));
    setCameraMode(null);
    setWizardStep(4);
  };

  const typeColors = {
    office: 'bg-blue-100 text-blue-700 border-blue-200',
    branch: 'bg-purple-100 text-purple-700 border-purple-200',
    hub: 'bg-amber-100 text-amber-700 border-amber-200',
    warehouse: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  return (
    <div data-testid="business-places-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Business Places</h1>
          <p className="text-sm text-slate-500 mt-1">{places.length} approved locations</p>
        </div>
        <Button data-testid="add-place-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
          <Plus className="w-4 h-4" /> Add Location
        </Button>
      </div>

      <div className="relative max-w-md">
        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="place-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or city..." className="pl-9" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(p => (
          <Card key={p.id} data-testid={`place-card-${p.id}`} className="border border-border/60 shadow-sm hover:shadow-md transition-all group">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {p.selfie_photo ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-border/40">
                      <img src={p.selfie_photo} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-base font-bold text-slate-900">{p.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`text-[10px] ${typeColors[p.place_type] || typeColors.office}`}>{p.place_type}</Badge>
                      {p.zone && <span className="text-xs text-slate-400">{p.zone}</span>}
                    </div>
                  </div>
                </div>
                <Badge variant={p.approval_status === 'approved' ? 'default' : 'secondary'}
                  className={`text-xs ${p.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : ''}`}>
                  {p.approval_status || p.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span>{p.address}{p.address && p.city ? ', ' : ''}{p.city}</span>
              </div>
              {p.lat && p.lng && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Navigation className="w-3 h-3" />
                  {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                </div>
              )}
              {p.contact_person && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{p.contact_person}</span>
                </div>
              )}
              {p.contact_phone && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>{p.contact_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-border/30">
                <Button data-testid={`edit-place-${p.id}`} variant="ghost" size="sm" onClick={() => openEdit(p)} className="text-xs hover:bg-amber-50 hover:text-amber-700">
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button data-testid={`delete-place-${p.id}`} variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-xs hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">No business places found</div>
        )}
      </div>

      {/* Add/Edit Dialog - Multi-step Wizard */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="place-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {editing ? 'Edit Business Location' : `Add Location - Step ${wizardStep} of 4`}
            </DialogTitle>
          </DialogHeader>

          {/* Step Indicators */}
          {!editing && (
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className={`flex-1 h-1.5 rounded-full transition-colors ${s <= wizardStep ? 'bg-amber-500' : 'bg-slate-200'}`} />
              ))}
            </div>
          )}

          {/* Step 1: Name & Details */}
          {(wizardStep === 1 || editing) && !cameraMode && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Location Name *</Label>
                <Input data-testid="place-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name this business location" />
              </div>
              {form.lat && form.lng && (
                <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800">Location: {form.lat.toFixed(6)}, {form.lng.toFixed(6)}</span>
                </div>
              )}
              {(editing || wizardStep === 4) && (
                <>
                  <div className="col-span-2"><Label>Address</Label><Input data-testid="place-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" /></div>
                  <div><Label>City</Label><Input data-testid="place-city-input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" /></div>
                  <div><Label>Zone</Label><Input data-testid="place-zone-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} placeholder="Zone" /></div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.place_type} onValueChange={v => setForm({ ...form, place_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="branch">Branch</SelectItem>
                        <SelectItem value="hub">Hub</SelectItem>
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Contact</Label><Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} placeholder="Contact name" /></div>
                  <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="Phone" /></div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Selfie Camera */}
          {wizardStep === 2 && !editing && !cameraMode && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-slate-600">Take a selfie at the business location. Your camera and GPS location will be captured.</p>
              {form.selfie_photo ? (
                <div className="space-y-3">
                  <div className="rounded-lg overflow-hidden border"><img src={form.selfie_photo} alt="Selfie" className="w-full" /></div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setCameraMode('selfie')} className="flex-1">Retake</Button>
                    <Button onClick={() => setWizardStep(3)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">Next</Button>
                  </div>
                </div>
              ) : (
                <Button data-testid="open-selfie-camera" onClick={() => setCameraMode('selfie')} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12">
                  <Camera className="w-5 h-5 mr-2" /> Open Front Camera
                </Button>
              )}
            </div>
          )}

          {/* Step 3: Place Photo */}
          {wizardStep === 3 && !editing && !cameraMode && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-slate-600">Take a photo of the business location using the rear camera.</p>
              {form.place_photo ? (
                <div className="space-y-3">
                  <div className="rounded-lg overflow-hidden border"><img src={form.place_photo} alt="Place" className="w-full" /></div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setCameraMode('place')} className="flex-1">Retake</Button>
                    <Button onClick={() => setWizardStep(4)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">Next</Button>
                  </div>
                </div>
              ) : (
                <Button data-testid="open-place-camera" onClick={() => setCameraMode('place')} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12">
                  <Camera className="w-5 h-5 mr-2" /> Open Rear Camera
                </Button>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {wizardStep === 4 && !editing && !cameraMode && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" /></div>
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" /></div>
                <div><Label>Zone</Label><Input value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} placeholder="Zone" /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.place_type} onValueChange={v => setForm({ ...form, place_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="branch">Branch</SelectItem>
                      <SelectItem value="hub">Hub</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Contact</Label><Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} placeholder="Contact name" /></div>
              </div>
              {/* Photo previews */}
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <p className="text-xs text-slate-400 mb-1">Selfie</p>
                  {form.selfie_photo ? <img src={form.selfie_photo} alt="Selfie" className="w-full rounded-lg border" /> : <div className="h-24 rounded-lg border border-dashed flex items-center justify-center text-slate-300"><Image className="w-6 h-6" /></div>}
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400 mb-1">Place Photo</p>
                  {form.place_photo ? <img src={form.place_photo} alt="Place" className="w-full rounded-lg border" /> : <div className="h-24 rounded-lg border border-dashed flex items-center justify-center text-slate-300"><Image className="w-6 h-6" /></div>}
                </div>
              </div>
              {form.lat && (
                <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded flex items-center gap-1">
                  <Navigation className="w-3 h-3" /> {form.lat.toFixed(6)}, {form.lng?.toFixed(6)}
                </div>
              )}
            </div>
          )}

          {/* Camera Overlay */}
          {cameraMode === 'selfie' && (
            <CameraCapture facingMode="user" label="Take Selfie (Front Camera)" onCapture={handleSelfieCapture} onClose={() => setCameraMode(null)} />
          )}
          {cameraMode === 'place' && (
            <CameraCapture facingMode="environment" label="Take Place Photo (Rear Camera)" onCapture={handlePlacePhotoCapture} onClose={() => setCameraMode(null)} />
          )}

          {/* Footer */}
          {!cameraMode && (
            <DialogFooter>
              {!editing && wizardStep > 1 && (
                <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}>Back</Button>
              )}
              {!editing && wizardStep < 2 && (
                <Button onClick={() => { if (!form.name) { toast.error('Name is required'); return; } setWizardStep(2); }} className="bg-amber-600 hover:bg-amber-700 text-white">
                  Next: Take Selfie
                </Button>
              )}
              {(editing || wizardStep === 4) && (
                <>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button data-testid="save-place-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">
                    {editing ? 'Update' : (currentUser?.role === 'business_head' ? 'Create & Approve' : 'Submit for Approval')}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
